import {ApiClient, type ErrorResponse} from '@hconnect/apiclient'
import {useUpmPlantContext} from '@hconnect/upm'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {
  FEATURE_NAMESPACE,
  OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY,
  getAnalyzeProductionPlanPath
} from '../consts'
import {notifyIfErrorMessage} from '../utils'

export const useAnalyzeProductionPlan = () => {
  const {plantId: plantCode} = useUpmPlantContext()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()

  const {mutate, isPending: isLoading} = useMutation<void, AxiosError<ErrorResponse>>({
    mutationFn: async () => {
      await ApiClient.axiosInstance.post<void>(getAnalyzeProductionPlanPath(plantCode), {})
    },
    onError: (error) => {
      spawnErrorToast(t('error.apology'))
      notifyIfErrorMessage(error.response?.data.detail, raiseError, spawnErrorToast)
    },
    onSuccess: () => {
      spawnSuccessToast(t('success.analyzeProductionPlan'))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY, {plantCode}]
      })
    }
  })

  const analyzeProductionPlan = useCallback(() => mutate(), [mutate])

  return {analyzeProductionPlan, isLoading}
}
